import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import Breadcrumb from '../../molecules/breadcrumb/breadcrumb';
import Headline from '../../atoms/headline/headline';
import Copy from '../../atoms/copy/copy';
import MixedCopy from '../../molecules/mixed-copy/mixedCopy';
import StyledStage from './landingStage.style';
import ShareBox from '../../molecules/share-box/shareBox';
import EventTeaser from '../../molecules/event-teaser/eventTeaser';
import Container from '../../layout/container/container';
import { LongArrowRight } from '../../icons/arrow/arrow';

const Image = dynamic(() => import(/* webpackChunkName: "image" */ '../../atoms/image/image'));

// TODO: Remove the intro matrix field after live is updated to simple fields
const LandingStage = ({
  stageType = 'primary',
  intro = {
    headline: '',
    subline: '',
    introCopy: '',
    cooperationLogo: null,
    socialMedia: null
  },
  titleImage,
  cooperationLogo,
  headline = '',
  subline = '',
  introCopy = '',
  breadcrumb,
  dateTeaser,
  extraTopic = { headline: '', copy: '', from: null, to: null },
  ...props
}) => (
  <StyledStage className={`stage--${stageType}`} {...props}>
    {titleImage && (
      <div className="stage__hero-background">
        <Image className="stage__hero-image" {...titleImage} lazy="default" />
        <div className="stage__hero-dots" />
      </div>
    )}
    <div className="stage__content">
      <div className="stage__container">
        {breadcrumb && <Breadcrumb entries={breadcrumb} />}
        <div className="stage__intro">
          <div className="stage__intro-container">
            <div className="stage__intro-content">
              {(cooperationLogo || intro.cooperationLogo) && (
                <div className="stage__intro-cooperation">
                  <Image
                    className="stage__intro-cooperation-logo"
                    {...(cooperationLogo || intro.cooperationLogo)}
                    lazy="default"
                  />
                </div>
              )}
              {(subline || intro.subline) && (
                <Headline tag="h3" className="stage__subline" subline>
                  {subline || intro.subline}
                </Headline>
              )}
              <Headline tag="h1" primary>
                {headline || intro.headline}
              </Headline>

              {(introCopy || intro.introCopy) && (
                <MixedCopy
                  focused
                  fullWidth
                  html={introCopy || intro.introCopy}
                  className="stage__introduction"
                />
              )}

              <ShareBox staticHor />

              {titleImage?.credits && (
                <div
                  className="stage__credits"
                  dangerouslySetInnerHTML={{ __html: titleImage.credits }}
                />
              )}
            </div>
            <div className="stage__intro-content-border" />
          </div>
        </div>
        {dateTeaser && (
          <div className="stage__event">
            <EventTeaser
              id={null}
              timeOnTop
              allDay={dateTeaser.allDay}
              headline={dateTeaser.headline}
              startDate={dateTeaser.from}
              endDate={dateTeaser.to}
              button={dateTeaser.buttonText}
              uri={dateTeaser.buttonLink}
              className="teaserContainer__teaser"
              copy={dateTeaser.copy}
            />
          </div>
        )}
      </div>
      {extraTopic && (
        <Container large className="stage__topic-container">
          <div className="stage__topic-grid">
            <div className="stage__topic-image-container">
              <Image className="stage__topic-image" {...extraTopic.image} lazy="default" />
              <div className="stage__topic-image-background">
                <div className="stage__topic-image-bg-color" />
                <div className="stage__topic-image-bg-dots" />
              </div>
            </div>
            <div className="stage__topic-text-container">
              {(extraTopic.subline || extraTopic.headline) && (
                <div className="stage__topic-headlines">
                  {extraTopic.subline && (
                    <Headline tag="h3" subline>
                      {extraTopic.subline}
                    </Headline>
                  )}
                  {extraTopic.headline && <Headline secondary>{extraTopic.headline}</Headline>}
                </div>
              )}
              <div className="stage__topic-copy">
                <Copy>{extraTopic.copy}</Copy>
                {extraTopic.linkTo && (
                  <a className="stage__topic-link" href={extraTopic.linkTo?.uri}>
                    <LongArrowRight role="presentation" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </Container>
      )}
    </div>
  </StyledStage>
);

LandingStage.propTypes = {
  stageType: PropTypes.oneOf(['primary', 'secondary']),
  cooperationLogo: PropTypes.shape(),
  headline: PropTypes.string,
  subline: PropTypes.string,
  introCopy: PropTypes.string,
  intro: PropTypes.shape({
    cooperationLogo: PropTypes.shape(),
    headline: PropTypes.string,
    subline: PropTypes.string,
    introCopy: PropTypes.string,
    socialMedia: PropTypes.bool
  }),
  titleImage: PropTypes.shape({
    credits: PropTypes.string
  }),
  breadcrumb: PropTypes.arrayOf(PropTypes.shape()),
  dateTeaser: PropTypes.shape({
    allDay: PropTypes.bool,
    headline: PropTypes.string,
    buttonText: PropTypes.string,
    buttonLink: PropTypes.string,
    copy: PropTypes.string,
    from: PropTypes.shape({
      d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      H: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      i: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      c: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    to: PropTypes.shape({
      d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      H: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      i: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      c: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  }),
  extraTopic: PropTypes.shape({
    image: PropTypes.shape({
      credits: PropTypes.string
    }),
    headline: PropTypes.string,
    subline: PropTypes.string,
    copy: PropTypes.string,
    linkTo: PropTypes.string
  })
};

const mapStateToProps = state => ({
  stageType: state.app.pageData.stageType,
  headline: state.app.pageData.headline,
  subline: state.app.pageData.subline,
  introCopy: state.app.pageData.introCopy,
  intro: state.app.pageData.intro,
  titleImage: state.app.pageData.titleImage,
  breadcrumb: state.app.pageData.breadcrumb,
  dateTeaser: state.app.pageData.dateTeaser,
  extraTopic: state.app.pageData.extraTopic
});

export { LandingStage as StageComponent };
export default connect(mapStateToProps)(LandingStage);
